import { useState, useEffect } from 'react';
import EngravingImage from './EngravingImage';
import './editorial.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';

// Local placeholder shown (through the engraving filter) until the hero-art API
// is live. Drop an image at frontend/public/hero-placeholder.jpg to use it; if
// the file is absent the component falls back to the dotted placeholder block.
const PLACEHOLDER_SRC = `${process.env.PUBLIC_URL || ''}/hero-placeholder.jpg`;

/**
 * HeroArt — full-width photo at the top of the Brief tab (public, above the
 * paywall). Image comes from the server-side swappable provider (Unsplash);
 * while the key is unset or the call fails it renders a local placeholder image
 * (or, if none is present, a labeled engraved-look block — no broken images).
 * Every image is run through the engraving filter, and a photo credit line
 * always renders (Unsplash terms).
 */
export default function HeroArt({ theme }) {
  const [art, setArt] = useState(null);
  const [phFailed, setPhFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const fallback = { placeholder: true, imageUrl: null, credit: 'Imagery pending' };
    fetch(`${API_URL}/api/hero-art?theme=${encodeURIComponent(theme || '')}`)
      .then((r) => (r.ok ? r.json() : fallback))
      .then((d) => { if (alive) setArt(d || fallback); })
      .catch(() => { if (alive) setArt(fallback); });
    return () => { alive = false; };
  }, [theme]);

  const hasApiImage = art && art.imageUrl;
  const usePlaceholderImg = !hasApiImage && !phFailed;
  const src = hasApiImage ? art.imageUrl : (usePlaceholderImg ? PLACEHOLDER_SRC : null);
  const credit = hasApiImage ? (art.credit || 'Imagery pending')
    : (usePlaceholderImg ? 'Placeholder imagery' : 'Imagery pending');

  return (
    <figure className="bi-hero">
      {src
        ? <EngravingImage
            src={src}
            alt={credit}
            onError={usePlaceholderImg ? () => setPhFailed(true) : undefined}
          />
        : <div className="bi-hero__placeholder" role="img" aria-label={credit} />}
      <figcaption className="bi-hero__credit">
        {hasApiImage && art.creditUrl
          ? <a href={art.creditUrl} target="_blank" rel="noopener noreferrer">{credit}</a>
          : credit}
      </figcaption>
    </figure>
  );
}
